import { MouseEvent } from 'react';
import { IntlShape, useIntl } from 'react-intl';

import { logger } from '../../../../core/Log/logger';
import { buildSearchSuggestionUrl } from '../../../../resources/searchSuggestions/domain/operations/buildSearchSuggestionUrl';
import { SearchSuggestion } from '../../../../resources/searchSuggestions/domain/types/searchSuggestions';
import { useUrlGenerator } from '../../../../shared/hooks/useUrlGenerator';
import { PickDomain } from '../../../../shared/utilities/types/PickDomain';

import { SearchResultItem } from './SearchResultItem';
import style from './SearchResultsState.module.css';

type SuggestionForResult = PickDomain<
    SearchSuggestion,
    'id' | 'type' | 'slug' | 'name' | 'coverPicture',
    {
        parentName: SearchSuggestion['parentName'] | null;
        parentDestinationSlug: SearchSuggestion['parentDestinationSlug'] | null;
        childRegionCount: SearchSuggestion['childRegionCount'] | null;
        agencyCount: SearchSuggestion['agencyCount'] | null;
    }
>;

export type SearchResultsStateProps = {
    suggestions: SuggestionForResult[];
    isDestinationWithRegions: boolean;
    activeIndex: number | null;
    pendingIndex: number | null;
    itemRef: (index: number, el: HTMLAnchorElement | null) => void;
    onMouseEnter: (index: number) => void;
    getOptionId: (index: number) => string;
    onClick: (
        event: MouseEvent<HTMLAnchorElement>,
        suggestion: SuggestionForResult,
        index: number,
    ) => void;
};

function buildResultMeta(
    formatMessage: IntlShape['formatMessage'],
    suggestion: SuggestionForResult,
): string | null {
    if (suggestion.type === 'destination') {
        const parts: string[] = [];
        if (suggestion.childRegionCount) {
            parts.push(
                formatMessage(
                    { id: 'global.search.overlay.meta.childRegion' },
                    { count: suggestion.childRegionCount },
                ),
            );
        }
        if (suggestion.agencyCount) {
            parts.push(
                formatMessage(
                    { id: 'global.search.overlay.meta.agency' },
                    { count: suggestion.agencyCount },
                ),
            );
        }
        return parts.length > 0 ? parts.join(' • ') : null;
    }
    return suggestion.type === 'region'
        ? formatMessage({ id: 'global.search.overlay.meta.typeRegion' })
        : formatMessage({ id: 'global.search.overlay.meta.typePlace' });
}

export function SearchResultsState({
    suggestions,
    isDestinationWithRegions,
    activeIndex,
    pendingIndex,
    itemRef,
    onMouseEnter,
    getOptionId,
    onClick,
}: SearchResultsStateProps) {
    const { formatMessage } = useIntl();
    const { generatePath } = useUrlGenerator();

    const renderable = suggestions.flatMap((suggestion) => {
        const url = buildSearchSuggestionUrl(suggestion, generatePath);
        if (url === null) {
            logger.error('SearchResultsState: suggestion has no resolvable URL', {
                id: suggestion.id,
                type: suggestion.type,
                slug: suggestion.slug,
            });
            return [];
        }
        return [{ suggestion, url }];
    });

    return (
        <div className={style.list}>
            {renderable.map(({ suggestion, url }, index) => {
                const isSubRegion = isDestinationWithRegions && suggestion.type === 'region';
                const meta = isSubRegion ? null : buildResultMeta(formatMessage, suggestion);
                return (
                    <SearchResultItem
                        key={suggestion.id}
                        id={getOptionId(index)}
                        ref={(el) => itemRef(index, el)}
                        suggestion={suggestion}
                        href={url}
                        isActive={activeIndex === index}
                        isSubRegion={isSubRegion}
                        meta={meta}
                        isPending={pendingIndex === index}
                        isDisabled={pendingIndex !== null}
                        onClick={(event) => {
                            event.preventDefault();
                            if (pendingIndex === null) onClick(event, suggestion, index);
                        }}
                        onMouseEnter={() => onMouseEnter(index)}
                    />
                );
            })}
        </div>
    );
}
